import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { ConfirmDialog } from "../../../../../components/ui/confirm-dialog";
import { createSortableHeader, DataTable } from "../../../../../components/ui/data-table";
import { useTriggerBaixa } from "../hooks/use-credito-adhoc";
import { CreditoAdhoc } from "../types";
import { formatDateBR } from "../../../../../utils/format-utils";

interface CreditsTableProps {
  data: CreditoAdhoc[];
  loading: boolean;
  dbName: string;
  onRefresh: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value / 100
  );

const formatDate = (value: string) => (value ? formatDateBR(value) : "—");

export function CreditsTable({ data, loading, dbName, onRefresh }: CreditsTableProps) {
  const triggerBaixa = useTriggerBaixa();
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    creditoAdhocId: number | null;
    solId: string;
  }>({ open: false, creditoAdhocId: null, solId: "" });

  const columns: ColumnDef<CreditoAdhoc>[] = [
    {
      accessorKey: "solId",
      header: createSortableHeader("Sol. ID"),
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.getValue("solId") ?? "—"}</span>
      ),
    },
    {
      accessorKey: "ops",
      header: createSortableHeader("Franquia"),
    },
    {
      accessorKey: "cedenteRazaoSocial",
      header: createSortableHeader("Cedente"),
    },
    {
      accessorKey: "dataVencimento",
      header: createSortableHeader("Vencimento"),
      cell: ({ row }) => formatDate(row.getValue("dataVencimento")),
    },
    {
      accessorKey: "dataRequisicao",
      header: createSortableHeader("Requisição"),
      cell: ({ row }) => formatDate(row.getValue("dataRequisicao")),
    },
    {
      accessorKey: "valorFace",
      header: createSortableHeader("Valor Face"),
      cell: ({ row }) => formatCurrency(row.getValue("valorFace")),
    },
    {
      accessorKey: "valorCompra",
      header: createSortableHeader("Valor Compra"),
      cell: ({ row }) => formatCurrency(row.getValue("valorCompra")),
    },
    {
      id: "status",
      header: "Status",
      enableSorting: false,
      cell: ({ row }) => {
        const { comprado, baixado } = row.original;
        if (baixado)
          return <Badge variant="secondary">Baixado</Badge>;
        if (comprado)
          return <Badge className="bg-green-600 text-white">Comprado</Badge>;
        return <Badge variant="outline">Pendente</Badge>;
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        const { comprado, baixado, id, solId } = row.original;
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={!comprado || baixado || triggerBaixa.isPending}
            onClick={() =>
              setConfirmState({ open: true, creditoAdhocId: id, solId: solId ?? "" })
            }
          >
            Gerar Baixa CNAB
          </Button>
        );
      },
    },
  ];

  const handleConfirmBaixa = async () => {
    if (!confirmState.creditoAdhocId) return;
    await triggerBaixa.mutateAsync({
      dbName,
      creditoAdhocId: confirmState.creditoAdhocId,
    });
    setConfirmState({ open: false, creditoAdhocId: null, solId: "" });
    onRefresh();
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        title="Créditos AdHoc Cadastrados"
        onRefresh={onRefresh}
        searchPlaceholder="Buscar por Sol ID, franquia..."
        searchableColumns={["solId", "ops", "cedenteRazaoSocial"]}
      />
      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={(open) =>
          setConfirmState((s) => ({ ...s, open }))
        }
        title="Confirmar Geração de Baixa CNAB"
        description={`Deseja gerar o CNAB de baixa para o crédito ${confirmState.solId}? Esta ação não pode ser desfeita.`}
        onConfirm={handleConfirmBaixa}
        confirmDisabled={triggerBaixa.isPending}
      />
    </>
  );
}
